import type { FieldDecision } from './types'

/**
 * Where a migrating field's build stands.
 *
 * The guide records what was decided about each legacy field and, until this, stopped
 * there. A field decided Migrate or Migrate with Change still has to be built in the new
 * org, and the build is a second piece of state with its own answer: not yet, done, held up
 * on something, or not built after all. The field it was built as sits beside it, because a
 * field migrated with a change rarely keeps its legacy name, and whoever goes looking for it
 * has the new one.
 */
export const BUILD_STATUSES = ['To Build', 'Built', 'Open', 'Not Built'] as const

export type BuildStatus = typeof BUILD_STATUSES[number]

/** The two verdicts that take a field across to the new org — the only ones with a build. */
export const isMigrating = (f: Pick<FieldDecision, 'decision'>) =>
  f.decision === 'Migrate' || f.decision === 'Migrate with Change'

/**
 * Blank for a field that is not going across. A migrating field nobody has recorded yet is
 * To Build — it does need building, and a blank would read as though the question did not
 * apply. A verdict changed after the build was recorded drops the old status rather than
 * letting it keep counting.
 */
export function buildStatusOf(f: Pick<FieldDecision, 'decision' | 'buildStatus'>): BuildStatus | '' {
  if (!isMigrating(f)) return ''
  const said = f.buildStatus ?? ''
  return (BUILD_STATUSES as readonly string[]).includes(said) ? said as BuildStatus : 'To Build'
}

export const toneForBuild = (s: BuildStatus | '') =>
  s === 'Built' ? 'done' as const
  : s === 'Open' ? 'critical' as const
  : s === 'To Build' ? 'high' as const
  : 'neutral' as const

export type BuildProgress = {
  migrating: number; toBuild: number; built: number; open: number; notBuilt: number
  /** Built or Not Built — nothing left to do either way, so both finish the build. */
  done: number
}

/** Counted from the live rows, over the migrating fields only. */
export function buildProgress(fields: FieldDecision[]): BuildProgress {
  const p: BuildProgress = { migrating: 0, toBuild: 0, built: 0, open: 0, notBuilt: 0, done: 0 }
  for (const f of fields) {
    const s = buildStatusOf(f)
    if (s === '') continue
    p.migrating++
    if (s === 'Built') p.built++
    else if (s === 'Open') p.open++
    else if (s === 'Not Built') p.notBuilt++
    else p.toBuild++
  }
  p.done = p.built + p.notBuilt
  return p
}
